import { useEffect, useState } from 'react';
import { Wallet, TrendingUp, ListChecks } from 'lucide-react';

type DistributionRow = {
  id: string;
  admin_login_id?: string;
  admin_name?: string;
  recipient_type?: string;
  recipient_login_id?: string;
  recipient_name?: string;
  amount: number;
  notes?: string;
  distributed_at?: string;
};

type AccountData = {
  login_id: string;
  name: string;
  role: string;
  is_super_admin: boolean;
  wallet_balance: number | null;
  unlimited_balance: boolean;
  distributed_total: number;
  deducted_total: number;
  transfer_count: number;
  recent_distributions: DistributionRow[];
};

const money = (value: unknown) => Number(value || 0).toLocaleString();

export default function Account() {
  const [account, setAccount] = useState<AccountData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    fetch('/admin/account', { headers: { Authorization: `Bearer ${token || ''}` } })
      .then(async (res) => {
        const payload = await res.json();
        if (!res.ok) throw new Error(payload?.error?.message || 'Failed to load account');
        setAccount(payload);
      })
      .catch((err) => setError(err.message || 'Failed to load account'));
  }, []);

  if (error) return <div className="p-6 text-red-700 bg-red-50">{error}</div>;
  if (!account) return <div className="p-6 text-gray-600">Loading account...</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold text-gray-900">Account</h2>
        <p className="text-sm text-gray-600">{account.name} | {account.login_id} | {account.is_super_admin ? 'Super Admin' : 'Admin'}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-700"><Wallet size={18} /> Left Balance</div>
          <div className="mt-2 text-2xl font-bold text-emerald-950">
            {account.unlimited_balance ? 'Unlimited' : `${money(account.wallet_balance)} credits`}
          </div>
        </div>
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-700"><TrendingUp size={18} /> Total Distributed</div>
          <div className="mt-2 text-2xl font-bold text-amber-950">{money(account.distributed_total)} credits</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><ListChecks size={18} /> Transfers</div>
          <div className="mt-2 text-2xl font-bold text-slate-950">{money(account.transfer_count)}</div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-700">
            <tr>
              <th className="p-3">Date</th>
              <th className="p-3">From</th>
              <th className="p-3">To</th>
              <th className="p-3">Type</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {account.recent_distributions.map((row) => (
              <tr key={row.id} className="border-t">
                <td className="p-3">{row.distributed_at ? new Date(row.distributed_at).toLocaleString() : '-'}</td>
                <td className="p-3">{row.admin_name || row.admin_login_id || '-'}</td>
                <td className="p-3">{row.recipient_name || row.recipient_login_id || '-'}</td>
                <td className="p-3 capitalize">{row.recipient_type || '-'}</td>
                <td className={`p-3 text-right font-semibold ${Number(row.amount) < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{money(row.amount)}</td>
                <td className="p-3 text-gray-600">{row.notes || '-'}</td>
              </tr>
            ))}
            {!account.recent_distributions.length && (
              <tr><td className="p-6 text-center text-gray-500" colSpan={6}>No credit distribution yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
